/**
 * The seam that makes the assistant agent-agnostic.
 *
 * Everything above this file — the panel, the tools, the walkthroughs, the
 * knowledge of the app — knows nothing about Claude or GPT or anything else. It
 * knows `AssistantProvider`: hand it a conversation and a list of tools, get
 * back either words or tool calls. That is the entire contract.
 *
 * It is shaped this way for the same reason the app talks to eight repository
 * interfaces instead of to a database: the thing behind the seam is going to
 * change, probably more than once, and none of it should reach the screens.
 * Swapping Anthropic for OpenAI, or for a model on the family's own server, or
 * for something that does not exist yet, is writing one file in this folder.
 *
 * Note what is *not* in the interface: streaming, model names, temperature, API
 * shapes, retries. Those are each provider's business. The interface carries
 * only what the app actually needs to show an answer and act on it.
 */

export type Role = 'user' | 'assistant';

/** One turn. A turn from the assistant may be words, tool calls, or both. */
export interface Turn {
  role: Role;
  text?: string;
  /** Tool calls the assistant made on this turn. */
  calls?: ToolCall[];
  /** Results the app fed back, in the same order as a previous turn's calls. */
  results?: ToolResult[];
  /**
   * Whatever the provider needs back verbatim on the next round — opaque here.
   * The app's own server uses it to return the model's reasoning unchanged.
   */
  providerData?: unknown;
}

export interface ToolCall {
  /** The provider's own id for this call, echoed back with the result. */
  id: string;
  name: string;
  input: Record<string, unknown>;
}

export interface ToolResult {
  id: string;
  /** What the app did, as short text the model can read. */
  output: string;
  isError?: boolean;
}

/** A tool, described once, in nobody's dialect. Each provider converts it. */
export interface ToolDef {
  name: string;
  description: string;
  schema: {
    type: 'object';
    properties: Record<string, unknown>;
    required?: string[];
  };
}

export interface AskRequest {
  system: string;
  turns: Turn[];
  tools: ToolDef[];
  signal?: AbortSignal;
}

export interface AskReply {
  text: string;
  calls: ToolCall[];
  /** Echo this on the assistant turn built from this reply. */
  providerData?: unknown;
}

export interface AssistantProvider {
  /** For the settings screen and for error messages. */
  readonly id: string;
  readonly label: string;
  /**
   * Whether this provider can be used at all right now — a missing key, a
   * missing address. The panel asks before it lets someone type.
   */
  ready(): boolean;
  /** Why it is not ready, as a message key. */
  notReadyKey(): 'assistant.error.noKey' | 'assistant.error.noUrl' | null;
  ask(req: AskRequest): Promise<AskReply>;
  /**
   * Get ready for a question that is about to be asked — called the moment
   * somebody presses Help, while they are still talking. Fire and forget; a
   * provider with nothing to prepare leaves it out.
   */
  warm?(req: Omit<AskRequest, 'turns' | 'signal'>): void;
}

/**
 * Thrown for anything the person needs to read: a bad key, a rate limit, no
 * network.
 *
 * Two audiences, so two fields. `messageKey` is what a grandmother sees — a
 * sentence in her language that never mentions HTTP. `status` and `detail` are
 * what the administrator sees on the Assistant screen, because "something went
 * wrong" is not a thing anybody can fix. A key that is refused for no credit,
 * a model name that does not exist, and a browser blocking the call outright
 * all looked identical before these were carried up.
 */
export class ProviderError extends Error {
  constructor(
    message: string,
    /** A key in the dictionary, so the panel never shows a raw API error. */
    readonly messageKey: 'assistant.error.key' | 'assistant.error.rate' | 'assistant.error.network' | 'assistant.error.server' | 'assistant.error.unknown',
    readonly detail?: string,
    /** The HTTP status, when there was one. Absent for a network failure. */
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ProviderError';
  }
}

/**
 * Pull the sentence out of an error body.
 *
 * Anthropic answers `{"error":{"message":"…"}}`, OpenAI the same, and a proxy
 * may answer with plain text. Showing the sentence rather than the envelope is
 * the difference between an administrator fixing it and giving up.
 */
function reason(body: string): string {
  try {
    const j = JSON.parse(body) as { error?: { message?: string }; message?: string };
    const m = j.error?.message ?? j.message;
    if (typeof m === 'string' && m.trim()) return m.trim();
  } catch {
    /* not JSON — the raw text is the best we have */
  }
  return body.trim();
}

/** Turn an HTTP failure into something a person can act on. */
export async function httpError(res: Response): Promise<ProviderError> {
  let detail = '';
  try {
    detail = reason(await res.text()).slice(0, 400);
  } catch {
    /* body already consumed or unreadable */
  }
  if (res.status === 401 || res.status === 403) return new ProviderError('auth', 'assistant.error.key', detail, res.status);
  if (res.status === 429) return new ProviderError('rate limited', 'assistant.error.rate', detail, res.status);
  if (res.status >= 500) return new ProviderError('server', 'assistant.error.server', detail, res.status);
  return new ProviderError(`http ${res.status}`, 'assistant.error.unknown', detail, res.status);
}

/**
 * A network failure, told apart from a refusal by the server.
 *
 * In a browser this is very often not the network at all: it is the call never
 * being allowed to leave, which `fetch` reports as the same bare "Failed to
 * fetch" it uses for a flat wifi signal. The detail is kept so the
 * administrator screen can say which of the two it probably was.
 */
export function networkError(e: unknown): ProviderError {
  if (e instanceof ProviderError) return e;
  return new ProviderError(String(e), 'assistant.error.network', e instanceof Error ? e.message : String(e));
}
